import { lazy } from 'react';
import { Route } from 'react-router-dom';
import {
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { useReferralNavMenus } from './referral/navMenus';
import { usePatientNavMenus } from './patient/navMenus';
import { usePhoneTriageNavMenus } from './phoneTriage/navMenus';
import {
  useCareConnectSideMenus,
  useSettingsSideMenus,
} from 'src/components/SideMenus';
import { useAmbientSoloSideMenus } from 'src/modules/ambient_solo/SideMenus';
import { useCelloWidgetSideMenu } from 'src/modules/CelloWidget/SideMenu';
import { useSuperAdminSideMenu } from 'src/modules/SuperAdmin/SideMenus';
import { useTaskNavMenus } from './task/navMenus';
import { ButtonProps } from '@insight-health-ai/roseate/components';

export type AppNavMenu = {
  path: string;
  title: React.ReactNode;
  tooltip?: React.ReactNode;
  icon: React.ReactNode;
  onClick?: () => void;
  showBadge?: 'dot';
  submenu?: Pick<AppNavMenu, 'icon' | 'title' | 'path'>[];
  tourPopup?: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    content: string;
    primaryAction?: ButtonProps;
    secondaryAction?: ButtonProps;
  };
};

const PatientRoutes = lazy(() => import('./patient'));
const ReferralRoutes = lazy(() => import('./referral'));
const PhoneTriageRoutes = lazy(() => import('./phoneTriage'));
const FrontdeskRoutes = lazy(() => import('./frontdesk'));
const TaskRoutes = lazy(() => import('./task'));
const TagRoutes = lazy(() => import('./settings/tags'));

export const useAppRoutes = () => {
  const { isSuperAdmin } = useGetCurrentPractitioner();
  const { isEntitled: isPatientsModuleEntitled } =
    useFeatureEntitlements('patients-module');
  const { isEntitled: isFrontdeskModuleEntitled } =
    useFeatureEntitlements('frontdesk');
  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');

  const { isEntitled: hasPhoneTriageAccess } =
    useFeatureEntitlements('phone-triage');
  const { isEntitled: hasCallReadAccess } =
    usePermissionEntitlements('calls.read');
  const { isEntitled: isTasksModuleEntitled } = useFeatureEntitlements('tasks');

  const isPhoneTriageModuleEntitled = hasPhoneTriageAccess && hasCallReadAccess;

  const isTagModuleEntitled =
    isPhoneTriageModuleEntitled || isReferralsModuleEntitled || isSuperAdmin;

  return [
    isPatientsModuleEntitled && (
      <Route key="patients" path="/patients/*" element={<PatientRoutes />} />
    ),
    isReferralsModuleEntitled && (
      <Route key="referrals" path="/referrals/*" element={<ReferralRoutes />} />
    ),
    isPhoneTriageModuleEntitled && (
      <Route
        key="phone-triage"
        path="/phone-triage/*"
        element={<PhoneTriageRoutes />}
      />
    ),
    isFrontdeskModuleEntitled && (
      <Route
        key="frontdesk"
        path="/frontdesk/*"
        element={<FrontdeskRoutes />}
      />
    ),
    isTagModuleEntitled && (
      <Route key="tags" path="/settings/tags/*" element={<TagRoutes />} />
    ),
    isTasksModuleEntitled && (
      <Route key="tasks" path="/tasks/*" element={<TaskRoutes />} />
    ),
  ].filter(Boolean);
};

export type CommonNavMenuHookParams = {
  activeMenu: string;
};

export const useAppNavMenus = ({
  activeMenu,
}: CommonNavMenuHookParams): AppNavMenu[] => {
  const patientNavMenus = usePatientNavMenus();
  const referralNavMenus = useReferralNavMenus();
  const phoneTriageNavMenus = usePhoneTriageNavMenus();
  const taskNavMenus = useTaskNavMenus();
  const careConnectNavMenus = useCareConnectSideMenus();
  const ambientSoloNavMenus = useAmbientSoloSideMenus({ activeMenu });
  const superAdminNavMenus = useSuperAdminSideMenu();
  const celloWidgetNavMenus = useCelloWidgetSideMenu();
  const settingsNavMenus = useSettingsSideMenus();

  return [
    ...patientNavMenus,
    ...referralNavMenus,
    ...phoneTriageNavMenus,
    ...taskNavMenus,
    ...careConnectNavMenus,
    ...ambientSoloNavMenus,
    ...superAdminNavMenus,
    ...celloWidgetNavMenus,
    ...settingsNavMenus,
  ];
};
